import { ApplicationConfig, isDevMode, importProvidersFrom } from '@angular/core';
import { RouterModule } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import { reducers, metaReducers } from './reducers';
import { CommonEffects } from '@core/store/effects/common.effects';
import { StockCorrectionEffects } from './features/stock-correction/stores/stock-correction.effects';
import { TranslateModule, TranslateLoader, TranslatePipe } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { AuthModule } from '@auth0/auth0-angular';
import { environment } from '../environments/environment';
import { authInterceptor } from '@core/interceptors/auth/auth-functional-interceptor';

// LoadingInterceptor is class-based (implements HttpInterceptor).
// It MUST be registered via HTTP_INTERCEPTORS + withInterceptorsFromDi().
// Without this, the spinner never turns off because totalRequests is never tracked.
import { LoadingInterceptor } from '@core/interceptors/loading/loading.interceptor';
import { provideNativeDateAdapter } from '@angular/material/core';

export function createTranslateLoader(http: HttpClient) {
  return {
    getTranslation(lang: string) {
      const BASE_API_URL = `${environment.BASE_API_URL}api/`;
      console.log('[Translation] Loading from API:', `${BASE_API_URL}languages/${lang}/labels`);
      return http.get(`${BASE_API_URL}languages/${lang}/labels`);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(RouterModule.forRoot(routes)),
    provideAnimations(),
    provideNativeDateAdapter(),
    // withInterceptors      → registers the functional authInterceptor
    // withInterceptorsFromDi → enables class-based interceptors (LoadingInterceptor)
    // Both are required together
    provideHttpClient(
      withInterceptors([authInterceptor]),
      withInterceptorsFromDi()
    ),

    // Register LoadingInterceptor via the legacy DI token
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true
    },

    // NgRx — reducers now includes common, stock-correction, dashboard, consume
    provideStore(reducers, { metaReducers }),
    provideEffects([CommonEffects, StockCorrectionEffects]),

    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75
    }),

    // Auth0
    importProvidersFrom(
      AuthModule.forRoot({
        domain: environment.authDomain,
        clientId: environment.authClientId,
        authorizationParams: {
          redirect_uri: environment.redirectUri
        }
      })
    ),

    // Translation
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'EN',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      })
    ),

    // TranslatePipe in root so all standalone components can inject it
    TranslatePipe,
  ]
};